import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import isConnectivityError from "../../helpers/isConnectivityError";
import { setBackendOffline } from "../../helpers/backendStatus";
import moment from "moment";

const decodeToken = (token) => {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload));
  } catch (err) {
    return null;
  }
};

// Token novo do mesmo usuário na mesma empresa (ex.: o login desta própria aba
// substituiu a sessão anterior): dá para seguir com ele sem recarregar a página.
const isSameSession = (tokenA, tokenB) => {
  const a = decodeToken(tokenA);
  const b = decodeToken(tokenB);
  return !!a && !!b && a.id === b.id && a.companyId === b.companyId;
};

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  const socketRef = useRef(null);

  // Ponto único de encerramento de sessão: token, usuário e socket saem juntos,
  // para a tela nunca ficar com usuário carregado e sem token.
  // staleToken: token que a falha usou. Se o localStorage já tem outro token,
  // este navegador já logou de novo: se for o mesmo usuário/empresa segue com o
  // token novo (retorna true), senão recarrega em vez de deslogar.
  // Sem toast: sessão encerrada só leva para o login.
  const endSession = ({ staleToken } = {}) => {
    const rawToken = localStorage.getItem("token");
    const currentToken = rawToken ? JSON.parse(rawToken) : null;
    if (staleToken && currentToken && currentToken !== staleToken) {
      if (isSameSession(staleToken, currentToken)) {
        api.defaults.headers.Authorization = `Bearer ${currentToken}`;
        return true;
      }
      window.location.reload();
      return false;
    }

    localStorage.removeItem("token");
    localStorage.removeItem("cshow");
    api.defaults.headers.Authorization = undefined;
    if (socketRef.current && typeof socketRef.current.disconnect === "function") {
      socketRef.current.disconnect();
    }
    socketRef.current = null;
    setSocket(null);
    setUser({});
    setIsAuth(false);
    history.push("/login");
  };
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;

  // Interceptors registrados uma vez (antes eram re-registrados a cada render)
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
          setIsAuth(true);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        setBackendOffline(false);
        return response;
      },
      async (error) => {
        if (isConnectivityError(error)) {
          setBackendOffline(true);
          return Promise.reject(error);
        }
        setBackendOffline(false);
        const originalRequest = error.config;
        if (error?.response?.status === 403 && !originalRequest._retry) {
          originalRequest._retry = true;

          const { data } = await api.post("/auth/refresh_token");
          if (data) {
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
          }
          return api(originalRequest);
        }
        // 401 do próprio login (senha errada, fora do horário) não é sessão encerrada
        if (error?.response?.status === 401 && !originalRequest?.url?.includes("/auth/login")) {
          const authHeader = originalRequest?.headers?.Authorization || "";
          const resumed = endSessionRef.current({ staleToken: authHeader.split(" ")[1] });
          // Saiu com o token anterior ao login novo: repete com o token atual
          if (resumed && !originalRequest._retry) {
            originalRequest._retry = true;
            return api(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );

    // Logout em outra aba apaga o token compartilhado: esta aba sai junto
    const handleStorage = (event) => {
      if ((event.key === "token" || event.key === null) && !localStorage.getItem("token")) {
        endSessionRef.current();
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user || data);
        } catch (err) {
          // Sessão inválida já cai no login; backend fora do ar só aparece na sidebar
          console.warn("Falha ao renovar sessão:", err);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        socketRef.current = socketInstance;
        setSocket(socketInstance);

        // Usuário logou em outro lugar. Espera um pouco para, se o novo login foi
        // em outra aba deste navegador, o token novo já estar no localStorage.
        const handleSessionReplaced = (staleToken) => {
          setTimeout(() => {
            const resumed = endSessionRef.current({
              staleToken: staleToken ? staleToken.split(" ")[1] : undefined,
            });
            if (resumed) socketInstance.resumeSession();
          }, 1500);
        };
        socketInstance.offConnectionEvent("session_replaced");
        socketInstance.onConnectionEvent("session_replaced", handleSessionReplaced);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
            console.log(`Listener adicionado para: ${eventName}`);
          } else {
            console.error("Socket instance não tem método 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        if (setting && setting.value === "enabled") {
          const signEnable = setting.value === "enable";
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      if (data.user.profileImage) {
        localStorage.setItem("profileImage", data.user.profileImage);
      } else {
        localStorage.removeItem("profileImage");
      }

      moment.locale("pt-br");
      let dueDate;
      if (!data.user.company || data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push("/tickets");
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleSelectCompany = async (companyId) => {
    const { data } = await api.put("/auth/select-company", { companyId });
    localStorage.setItem("token", JSON.stringify(data.token));
    window.location.reload();
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      // Backend fora do ar não pode prender o usuário logado: sai localmente
      endSessionRef.current();
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleSelectCompany,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;